import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Alert 
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faCheckCircle, faLock, faCircle } from '@fortawesome/free-solid-svg-icons';
import { get, post } from 'helpers/requests';
import { BASE_URL, API } from 'CONST';

const IntermediarioAccionesModal = ({ aseguradoId }) => {
  const [modal, setModal] = useState(false);
  const [accionesTipos, setAccionesTipos] = useState([]);
  const [perPolicyPermissions, setPerPolicyPermissions] = useState({});
  const [selectedActions, setSelectedActions] = useState({});
  const [selectedPolicies, setSelectedPolicies] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [intermediarioId, setIntermediarioId] = useState(null);
  const [policies, setPolicies] = useState([]);

  const toggle = () => {
    setModal(!modal);
    if (!modal) fetchAllData();
  };

  const fetchIntermediarioData = async () => {
    try {
      const response = await get(`${BASE_URL}${API.polizasCredito}?asegurado_id=${aseguradoId}`);
      const endosos = response || [];

      if (!endosos.length) throw new Error('No se encontraron pólizas');
      const firstEndoso = endosos[0];
      if (!firstEndoso.intermediario_id) throw new Error('No se encontró intermediario_id');

      const allPolicies = endosos
        .filter(e => e.nemotecnico)
        .map(e => ({ nemotecnico: e.nemotecnico }));

      if (!allPolicies.length) throw new Error('No se encontraron nemotécnicos en las pólizas');

      setIntermediarioId(firstEndoso.intermediario_id);
      setPolicies(allPolicies);

      const defaultSelected = {};
      allPolicies.forEach(({ nemotecnico }) => { defaultSelected[nemotecnico] = true; });
      setSelectedPolicies(defaultSelected);

      return { intermediarioId: firstEndoso.intermediario_id, policies: allPolicies };
    } catch (err) {
      throw new Error('Error al obtener datos de la póliza: ' + (err.message || ''));
    }
  };

  const fetchAccionesTipos = async () => {
    try {
      const response = await get(`${BASE_URL}${API.accionesTipos}`);
      setAccionesTipos(response || []);
    } catch (err) {
      throw new Error('Error al cargar tipos de acciones: ' + (err.message || ''));
    }
  };

  const fetchCurrentPermissions = async (intermId, allPolicies) => {
    try {
      const responses = await Promise.all(
        allPolicies.map(({ nemotecnico }) =>
          post(`${BASE_URL}${API.accionesPermitidas}get_user_actions/`, {
            organization_id: intermId,
            policy_nemotecnico: nemotecnico,
          })
        )
      );

      const perPolicy = {};
      responses.forEach((response, idx) => {
        const nemotecnico = allPolicies[idx].nemotecnico;
        perPolicy[nemotecnico] = {};

        if (!response.acciones) return;
        response.acciones.forEach(accion => {
          if (accion.tipo_permiso === 'ESPECIFICO' && accion.policy_nemotecnico) {
            perPolicy[nemotecnico][accion.accion.id] = accion.is_active;
          }
        });
      });

      setPerPolicyPermissions(perPolicy);

      const allNemotecnicos = allPolicies.map(p => p.nemotecnico);
      const selected = {};

      const allAccionIds = new Set(
        allNemotecnicos.flatMap(nem => Object.keys(perPolicy[nem] || {}))
      );

      allAccionIds.forEach(accionId => {
        selected[accionId] = allNemotecnicos.every(nem => {
          const policyPerms = perPolicy[nem] || {};
          return accionId in policyPerms ? policyPerms[accionId] : false;
        });
      });

      setSelectedActions(selected);
    } catch (err) {
      throw new Error('Error al cargar permisos actuales: ' + (err.message || ''));
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { intermediarioId: intermId, policies: allPolicies } = await fetchIntermediarioData();
      await fetchAccionesTipos();
      await fetchCurrentPermissions(intermId, allPolicies);
    } catch (err) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAction = (accionId) => {
    setSelectedActions(prev => ({ ...prev, [accionId]: !prev[accionId] }));
    setSuccess(false);
  };

  const handleTogglePolicy = (nemotecnico) => {
    setSelectedPolicies(prev => ({ ...prev, [nemotecnico]: !prev[nemotecnico] }));
    setSuccess(false);
  };

  const handleSelectAll = (value) => {
    const newSelected = {};
    accionesTipos.forEach(accion => { newSelected[accion.id] = value; });
    setSelectedActions(newSelected);
  };

  const isActionChangedForSelectedPolicies = (accionId) => {
    const newValue = selectedActions[accionId] || false;
    const activePolicyKeys = policies
      .filter(({ nemotecnico }) => selectedPolicies[nemotecnico])
      .map(({ nemotecnico }) => nemotecnico);

    return activePolicyKeys.some(nem => {
      const current = (perPolicyPermissions[nem] || {})[accionId];
      const currentValue = current !== undefined ? current : false;
      return currentValue !== newValue;
    });
  };

  const getStatusForSelectedPolicies = (accionId) => {
    const activePolicyKeys = policies
      .filter(({ nemotecnico }) => selectedPolicies[nemotecnico])
      .map(({ nemotecnico }) => nemotecnico);

    if (!activePolicyKeys.length) return null;

    const states = activePolicyKeys.map(nem => {
      const perm = (perPolicyPermissions[nem] || {})[accionId];
      return perm !== undefined ? perm : null;
    });

    const hasActive = states.some(s => s === true);
    const hasInactive = states.some(s => s === false);
    const hasNew = states.some(s => s === null);

    if (hasNew && !hasActive && !hasInactive) return 'new';
    if (hasActive && !hasInactive && !hasNew) return 'active';
    if (!hasActive && !hasNew) return 'inactive';
    return 'mixed';
  };

  const handleSaveAll = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const activePolicies = policies.filter(({ nemotecnico }) => selectedPolicies[nemotecnico]);

      if (!activePolicies.length) {
        setError('Debes seleccionar al menos una póliza');
        setTimeout(() => setError(null), 2000);
        return;
      }

      const savePromises = [];

      activePolicies.forEach(({ nemotecnico }) => {
        const policyPerms = perPolicyPermissions[nemotecnico] || {};
        const activeAccionesIds = [];
        const inactiveAccionesIds = [];

        accionesTipos.forEach(accion => {
          const newValue = selectedActions[accion.id] || false;
          const currentValue = accion.id in policyPerms ? policyPerms[accion.id] : null;

          if (currentValue !== newValue) {
            if (newValue) activeAccionesIds.push(accion.id);
            else inactiveAccionesIds.push(accion.id);
          }
        });

        if (activeAccionesIds.length > 0) {
          savePromises.push(
            post(`${BASE_URL}${API.accionesPermitidas}bulk_create/`, {
              organization_id: intermediarioId,
              acciones_ids: activeAccionesIds,
              policy_nemotecnico: nemotecnico,
              is_active: true,
            })
          );
        }
        if (inactiveAccionesIds.length > 0) {
          savePromises.push(
            post(`${BASE_URL}${API.accionesPermitidas}bulk_create/`, {
              organization_id: intermediarioId,
              acciones_ids: inactiveAccionesIds,
              policy_nemotecnico: nemotecnico,
              is_active: false,
            })
          );
        }
      });

      if (!savePromises.length) {
        setError('No hay cambios para guardar');
        setTimeout(() => setError(null), 2000);
        return;
      }

      await Promise.all(savePromises);
      setSuccess(true);
      await fetchCurrentPermissions(intermediarioId, policies);

      setTimeout(() => {
        setSuccess(false);
        setModal(false);
      }, 2000);
    } catch (err) {
      setError('Error al guardar: ' + (err.message || ''));
    } finally {
      setIsSaving(false);
    }
  };

  if (!aseguradoId) return null;

  const selectedCount = Object.values(selectedActions).filter(v => v).length;
  const selectedPoliciesCount = Object.values(selectedPolicies).filter(v => v).length;

  return (
    <>
      <Button
        color="primary"
        outline
        onClick={toggle}
        className="btn-outline-orsan-logout"
        style={{ marginLeft: '10px' }}
      >
        <FontAwesomeIcon icon={faLock} className="fa-save" />
        Configuración del Corredor
      </Button>

      <Modal isOpen={modal} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          Configuración de Acciones del Corredor
        </ModalHeader>
        <ModalBody className="detalle-line-body">
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
          {success && <Alert color="success">✓ Acciones guardadas exitosamente</Alert>}

          {isLoading ? (
            <div className="centered-horizontal-container" style={{ padding: '40px' }}>
              <div className="sk-circle">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className={`sk-circle${i + 1} sk-child`}></div>
                ))}
              </div>
              <p style={{ marginTop: '10px', color: 'var(--orsan-dark-grey)' }}>Cargando acciones...</p>
            </div>
          ) : (
            <>
              {/* Policies selection section */}
              <div className="form-box" style={{ padding: '16px', margin: '0 0 20px 0' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                }}>
                  <div className="label" style={{ marginBottom: 0 }}>
                    Pólizas a configurar
                  </div>
                  <span style={{ fontSize: '13px', color: 'var(--orsan-grey)' }}>
                    {selectedPoliciesCount} de {policies.length} seleccionada{policies.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {policies.map(({ nemotecnico }) => {
                    const isChecked = selectedPolicies[nemotecnico] || false;
                    return (
                      <label
                        key={nemotecnico}
                        className="nav-pill"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: isChecked ? 'var(--orsan-blue)' : '#f9fafc',
                          color: isChecked ? 'white' : 'var(--orsan-dark-grey)',
                          padding: '6px 14px',
                          fontSize: '13px',
                          fontWeight: isChecked ? '600' : '400',
                          opacity: isChecked ? 1 : 0.85,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleTogglePolicy(nemotecnico)}
                          style={{ display: 'none' }}
                        />
                        <FontAwesomeIcon 
                          icon={isChecked ? faCheckCircle : faCircle} 
                          style={{ 
                            marginRight: '6px', 
                            fontSize: '11px',
                            opacity: isChecked ? 1 : 0.5 
                          }} 
                        />
                        {nemotecnico}
                      </label>
                    );
                  })}
                </div>

                {selectedPoliciesCount === 0 && (
                  <div className="error-msg" style={{ marginTop: '10px' }}>
                    Selecciona al menos una póliza para guardar
                  </div>
                )}
              </div>

              {/* Actions table */}
              {accionesTipos.length === 0 ? (
                <Alert color="warning">No se encontraron tipos de acciones disponibles</Alert>
              ) : (
                <>
                  <div style={{ 
                    marginBottom: '12px', 
                    fontSize: '13px', 
                    color: 'var(--orsan-dark-grey)' 
                  }}>
                    <strong>Total Acciones:</strong> {accionesTipos.length} |{' '}
                    <strong>Seleccionadas:</strong> {selectedCount}
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <button 
                      className="table-link" 
                      onClick={() => handleSelectAll(true)}
                      style={{ marginRight: '10px' }}
                    >
                      Seleccionar Todas
                    </button>
                    <span style={{ color: 'var(--orsan-grey)' }}>|</span>
                    <button 
                      className="table-link" 
                      onClick={() => handleSelectAll(false)}
                      style={{ marginLeft: '10px' }}
                    >
                      Deseleccionar Todas
                    </button>
                  </div>

                  <div className="archivo-table" style={{ border: '1px solid #CCCCCC' }}>
                    <table className="table table-hover mb-0" style={{ marginBottom: 0 }}>
                      <thead style={{ backgroundColor: '#EFEFEF' }}>
                        <tr>
                          <th width="60" style={{ textAlign: 'center', padding: '10px 5px' }}>
                            <input
                              type="checkbox"
                              checked={accionesTipos.length > 0 && selectedCount === accionesTipos.length}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          </th>
                          <th className="table-header" style={{ padding: '10px 5px' }}>Acción</th>
                          <th width="120" className="table-header" style={{ textAlign: 'center', padding: '10px 5px' }}>
                            Estado actual
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {accionesTipos.map(accion => {
                          const isSelected = selectedActions[accion.id] || false;
                          const hasChanged = isActionChangedForSelectedPolicies(accion.id);
                          const status = getStatusForSelectedPolicies(accion.id);

                          return (
                            <tr
                              key={accion.id}
                              className="table-cell"
                              style={{
                                backgroundColor: isSelected ? '#f9fafc' : 'white',
                                cursor: 'pointer',
                                borderLeft: hasChanged ? '3px solid var(--orsan-orange)' : 'none',
                                transition: 'background-color 0.15s ease',
                              }}
                              onClick={() => handleToggleAction(accion.id)}
                            >
                              <td style={{ textAlign: 'center', padding: '8px 5px' }} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleAction(accion.id)}
                                />
                              </td>
                              <td style={{ padding: '8px 5px' }}>
                                <strong style={{ color: 'var(--orsan-black)' }}>{accion.nombre}</strong>
                                {hasChanged && (
                                  <span style={{
                                    marginLeft: '10px',
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    backgroundColor: '#fff3cd',
                                    color: '#856404',
                                    fontWeight: '600',
                                  }}>
                                    MODIFICADO
                                  </span>
                                )}
                              </td>
                              <td style={{ textAlign: 'center', padding: '8px 5px' }}>
                                {status === 'active' && (
                                  <span style={{ 
                                    fontSize: '11px', 
                                    padding: '3px 8px', 
                                    borderRadius: '4px', 
                                    backgroundColor: '#d4edda', 
                                    color: '#155724',
                                    fontWeight: '500',
                                  }}>
                                    Activo
                                  </span>
                                )}
                                {status === 'inactive' && (
                                  <span style={{ 
                                    fontSize: '11px', 
                                    padding: '3px 8px', 
                                    borderRadius: '4px', 
                                    backgroundColor: '#f8d7da', 
                                    color: '#721c24',
                                    fontWeight: '500',
                                  }}>
                                    Inactivo
                                  </span>
                                )}
                                {status === 'mixed' && (
                                  <span style={{ 
                                    fontSize: '11px', 
                                    padding: '3px 8px', 
                                    borderRadius: '4px', 
                                    backgroundColor: '#fff3cd', 
                                    color: '#856404',
                                    fontWeight: '500',
                                  }}>
                                    Mixto
                                  </span>
                                )}
                                {status === 'new' && (
                                  <span style={{ 
                                    fontSize: '11px', 
                                    padding: '3px 8px', 
                                    borderRadius: '4px', 
                                    backgroundColor: '#e2e3e5', 
                                    color: '#383d41',
                                    fontWeight: '500',
                                  }}>
                                    Nuevo
                                  </span>
                                )}
                                {status === null && (
                                  <span style={{ fontSize: '11px', color: 'var(--orsan-grey)' }}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ 
                    marginTop: '15px', 
                    fontSize: '12px', 
                    color: 'var(--orsan-dark-grey)',
                    fontStyle: 'italic',
                  }}>
                    <strong>Nota:</strong> El estado <em>Mixto</em> indica que las pólizas seleccionadas tienen configuraciones distintas para esa acción.
                    Los cambios se aplicarán únicamente a las pólizas seleccionadas arriba.
                  </div>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter className="uso-button-container" style={{ justifyContent: 'flex-end', gap: '12px' }}>
          <Button 
            className="modal-button form-button cancelar" 
            onClick={toggle}
          >
            Cancelar
          </Button>
          <Button
            className="form-button"
            onClick={handleSaveAll}
            disabled={isSaving || isLoading || accionesTipos.length === 0 || selectedPoliciesCount === 0}
            style={{ 
              minWidth: '180px',
              backgroundColor: 'var(--orsan-blue)',
              borderColor: 'var(--orsan-blue)',
              color: 'white',
              opacity: (isSaving || isLoading || accionesTipos.length === 0 || selectedPoliciesCount === 0) ? 0.7 : 1,
            }}
          >
            {isSaving ? (
              <>
                <div className="sk-circle" style={{ 
                  width: '20px', 
                  height: '20px', 
                  margin: '0 8px 0 0',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }}>
                  <div className="sk-circle1 sk-child"></div>
                </div>
                Guardando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="fa-save" style={{ color: 'white' }} />
                Guardar en {selectedPoliciesCount} póliza{selectedPoliciesCount !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default IntermediarioAccionesModal;